import React from "react";
import "./AgentTemplateSprite.css";

// Deterministically generates a unique, flowing gradient "sprite" from a seed
// string (the template name/id), so every template gets a distinct, stable
// icon without hand-authored assets. The look is a soft diagonal gradient with
// a couple of blurred color orbs positioned from the seed.

function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

// Corner radius in the fixed 0..100 viewBox space (mirrors `border-radius` in
// AgentTemplateSprite.css). Independent of the rendered `size`.
const CORNER_RADIUS = 24;

// Small seeded PRNG so every derived value (hues, positions) is stable per seed.
function makeRng(seed) {
  let state = seed % 2147483647;
  if (state <= 0) state += 2147483646;
  return () => {
    state = (state * 16807) % 2147483647;
    return (state - 1) / 2147483646;
  };
}

export default function AgentTemplateSprite({
  seed = "",
  groupSeed = "",
  size = 36,
}) {
  const uid = React.useId();
  const rng = makeRng(hashString(seed) + 1);

  // The group (category) sets the shared base hue; the per-template seed only
  // nudges it slightly and varies the gradient angle and orb layout, so cards
  // in the same group read as a family while staying individually distinct.
  const groupHue = hashString(groupSeed || seed) % 360;
  const hueA = (groupHue + Math.floor(rng() * 24) - 12 + 360) % 360;
  const hueB = (hueA + 26 + Math.floor(rng() * 18)) % 360;
  const hueC = (groupHue + 300 + Math.floor(rng() * 30)) % 360;

  const angle = rng() * Math.PI * 2;
  const x1 = 0.5 - Math.cos(angle) / 2;
  const y1 = 0.5 - Math.sin(angle) / 2;
  const x2 = 0.5 + Math.cos(angle) / 2;
  const y2 = 0.5 + Math.sin(angle) / 2;

  const orb1 = { cx: 0.2 + rng() * 0.45, cy: 0.18 + rng() * 0.4, r: 0.4 + rng() * 0.25 };
  const orb2 = { cx: 0.4 + rng() * 0.45, cy: 0.5 + rng() * 0.4, r: 0.35 + rng() * 0.25 };

  const clipId = `clip-${uid}`;
  const baseId = `base-${uid}`;
  const orb1Id = `orb1-${uid}`;
  const orb2Id = `orb2-${uid}`;
  const blurId = `blur-${uid}`;

  return (
    <svg
      className="agent-template-sprite"
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-hidden="true"
      style={{
        flexShrink: 0,
        display: "block",
        "--sprite-glow": `hsla(${groupHue}, 85%, 60%, 0.55)`,
      }}
    >
      <defs>
        <clipPath id={clipId}>
          <rect
            width="100"
            height="100"
            rx={CORNER_RADIUS}
            ry={CORNER_RADIUS}
          />
        </clipPath>
        <linearGradient id={baseId} x1={x1} y1={y1} x2={x2} y2={y2}>
          <stop offset="0%" stopColor={`hsl(${hueA}, 78%, 70%)`} />
          <stop offset="100%" stopColor={`hsl(${hueB}, 72%, 50%)`} />
        </linearGradient>
        <radialGradient id={orb1Id}>
          <stop offset="0%" stopColor={`hsla(${hueC}, 95%, 78%, 0.95)`} />
          <stop offset="100%" stopColor={`hsla(${hueC}, 95%, 78%, 0)`} />
        </radialGradient>
        <radialGradient id={orb2Id}>
          <stop offset="0%" stopColor={`hsla(${hueB}, 92%, 86%, 0.85)`} />
          <stop offset="100%" stopColor={`hsla(${hueB}, 92%, 86%, 0)`} />
        </radialGradient>
        <filter id={blurId} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="6" />
        </filter>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect width="100" height="100" fill={`url(#${baseId})`} />
        <g className="agent-template-sprite__orbs" filter={`url(#${blurId})`}>
          <circle
            cx={orb1.cx * 100}
            cy={orb1.cy * 100}
            r={orb1.r * 100}
            fill={`url(#${orb1Id})`}
          />
          <circle
            cx={orb2.cx * 100}
            cy={orb2.cy * 100}
            r={orb2.r * 100}
            fill={`url(#${orb2Id})`}
          />
        </g>
      </g>
    </svg>
  );
}
